/* ═══════════════════════════════════════════════════════════════
   Forma zone drag — slot targeting, wired to the DOM (shared).

   ONE implementation behind both surfaces: the sheet's ZoneBandEdit (Forma
   Studio and the project editor) and the DropZone chip list. A token on either carries

     data-slot-token  data-slot-zone=<zoneKey>  data-slot-line=<n>
     data-raw-idx=<index in the zone array>      data-frag=only|first|mid|last

   and the surface hands this hook its root element plus a `columnAt` that
   says which zone column the pointer is over. The hook then, once per drag
   frame (rAF-coalesced — never per mouse move):

     1. reads the rendered token boxes (un-shifting any gap it opened earlier,
        so the measurement cannot chase its own animation),
     2. asks the pure `slotForPointer` which slot / new line / replace it is,
     3. slides the neighbours apart (a relative `left` offset — nothing
        re-lays out) and reports the caret / ring geometry for the surface
        to draw with its own kit classes.

   Replace is withheld until the pointer has rested on a token's centre for
   REPLACE_REST_MS — a drag passing through never replaces anything.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  gapRawForLine,
  gapX,
  columnOwning,
  DEFAULT_GAP,
  type ColumnLines,
  rawIndexForSlot,
  slotForPointer,
  stepRest,
  type RestState,
  type Box,
  type Slot,
} from './zoneSlots';
import { pickReleaseTarget } from './zoneSlots';
import { endZoneDrag, getZoneDrag } from './zoneDragSession';

export interface SlotColumn {
  zoneKey: string;
  /** How the column's tokens are aligned — decides which way the gap opens. */
  align: 'left' | 'center' | 'right';
  /** "left" / "center" / "right" — for the "Add to …" empty-column hint. */
  label: string;
}

export interface SlotDropTarget {
  zoneKey: string;
  /** `empty` = the column holds nothing yet: the surface's own column drop runs. */
  kind: 'slot' | 'newline-above' | 'newline-below' | 'replace' | 'empty';
  /** Insert position (slot / new line) or the replaced token (replace). */
  rawIdx: number;
  newLine: boolean;
}

export interface SlotUi {
  target: SlotDropTarget;
  /** Root-local, unscaled CSS px. */
  caret: { left: number; top: number; height: number } | null;
  ring: { left: number; top: number; width: number; height: number } | null;
  label: string | null;
}

/** Metrics are derived from the app's own scale and motion tokens, once per
 *  drag: hysteresis and the fallback gap follow the root font size (the UI
 *  scale dial), and Replace arms after `--duration-fast` of rest. */
interface DragMetrics {
  hystPx: number;
  gapPx: number;
  restMs: number;
}
function readDragMetrics(): DragMetrics {
  const cs = getComputedStyle(document.documentElement);
  const rem = parseFloat(cs.fontSize) || 16;
  const raw = cs.getPropertyValue('--duration-fast').trim();
  const ms = raw.endsWith('ms')
    ? parseFloat(raw)
    : raw.endsWith('s')
      ? parseFloat(raw) * 1000
      : NaN;
  return { hystPx: rem * 0.375, gapPx: rem * 3, restMs: Number.isFinite(ms) ? ms : 120 };
}

interface Measured {
  els: HTMLElement[][];
  lineBoxes: Box[];
  tokenBoxes: Box[][];
  gapRaw: number[][];
  tokRaw: number[][];
}

const hasPayload = (e: React.DragEvent) =>
  Array.from(e.dataTransfer.types).includes('application/json');

export function useZoneSlotDrag(opts: {
  rootRef: React.RefObject<HTMLElement | null>;
  enabled: boolean;
  columnAt: (clientX: number, rootRect: DOMRect) => SlotColumn | null;
  /** Every column of the surface (lets rendered tokens override the geometric third). */
  columns?: () => SlotColumn[];
  /** Called on a slot drop. Return true when handled. */
  onDropAt: (target: SlotDropTarget, e: React.DragEvent) => boolean;
}) {
  const { rootRef, enabled } = opts;
  const optsRef = React.useRef(opts);
  optsRef.current = opts;

  const [ui, setUi] = React.useState<SlotUi | null>(null);
  const [dragging, setDragging] = React.useState(false);
  const draggingRef = React.useRef(false);
  const pointer = React.useRef<{ x: number; y: number } | null>(null);
  const raf = React.useRef(0);
  const timer = React.useRef(0);
  const prevSlot = React.useRef<Slot | null>(null);
  const prevZone = React.useRef<string | null>(null);
  const rest = React.useRef<RestState | null>(null);
  const lastKey = React.useRef('');
  const lastTarget = React.useRef<SlotDropTarget | null>(null);
  const metrics = React.useRef<DragMetrics | null>(null);

  const clearShifts = React.useCallback(() => {
    rootRef.current?.querySelectorAll<HTMLElement>('[data-slot-token]').forEach((el) => {
      if (el.style.left) el.style.left = '';
    });
  }, [rootRef]);

  const clear = React.useCallback(() => {
    cancelAnimationFrame(raf.current);
    window.clearTimeout(timer.current);
    raf.current = 0;
    pointer.current = null;
    prevSlot.current = null;
    prevZone.current = null;
    rest.current = null;
    lastKey.current = '';
    lastTarget.current = null;
    metrics.current = null;
    draggingRef.current = false;
    clearShifts();
    setUi(null);
    setDragging(false);
  }, [clearShifts]);

  const evaluate = React.useCallback((): SlotDropTarget | null => {
    raf.current = 0;
    const root = rootRef.current;
    const p = pointer.current;
    if (!root || !p) return null;
    const rr = root.getBoundingClientRect();
    const scale = root.offsetWidth > 0 ? rr.width / root.offsetWidth : 1;
    let col = optsRef.current.columnAt(p.x, rr);
    // A column owns its tokens, the span to one gap past its last token, and
    // the trailing area up to the next column — never the geometric third alone.
    if (col) {
      const owned = new Map<string, ColumnLines>();
      root.querySelectorAll<HTMLElement>('[data-slot-token]').forEach((el) => {
        const z = el.dataset.slotZone;
        if (!z) return;
        const r = el.getBoundingClientRect();
        const sh = (parseFloat(getComputedStyle(el).left) || 0) * scale;
        const b = { left: r.left - sh, right: r.right - sh, top: r.top, bottom: r.bottom };
        const c = owned.get(z) ?? { zoneKey: z, lines: new Map<number, Box[]>() };
        const ln = Number(el.dataset.slotLine);
        c.lines.set(ln, [...(c.lines.get(ln) ?? []), b]);
        owned.set(z, c);
      });
      const session0 = getZoneDrag();
      const gapW =
        (session0 && session0.widthPx > 0
          ? session0.widthPx
          : (metrics.current ??= readDragMetrics()).gapPx) * scale;
      const owner = columnOwning([...owned.values()], p.x, p.y, gapW || DEFAULT_GAP);
      if (owner && owner !== col.zoneKey) {
        col = optsRef.current.columns?.().find((c) => c.zoneKey === owner) ?? col;
      }
    }
    if (!col) {
      clearShifts();
      lastTarget.current = null;
      setUi(null);
      return null;
    }

    // ── one measuring pass over this column's rendered tokens ──
    const m: Measured = { els: [], lineBoxes: [], tokenBoxes: [], gapRaw: [], tokRaw: [] };
    const byLine = new Map<number, HTMLElement[]>();
    root.querySelectorAll<HTMLElement>('[data-slot-token]').forEach((el) => {
      if (el.dataset.slotZone !== col.zoneKey) return;
      const ln = Number(el.dataset.slotLine);
      if (!byLine.has(ln)) byLine.set(ln, []);
      byLine.get(ln)!.push(el);
    });
    const lineIds = [...byLine.keys()].sort((a, b) => a - b);
    for (const id of lineIds) {
      const els = byLine.get(id)!;
      const boxes: Box[] = els.map((el) => {
        const r = el.getBoundingClientRect();
        // Un-shift: the gap this hook opened must not move the thing it measures.
        const sh = (parseFloat(getComputedStyle(el).left) || 0) * scale;
        return { left: r.left - sh, right: r.right - sh, top: r.top, bottom: r.bottom };
      });
      m.els.push(els);
      m.tokenBoxes.push(boxes);
      m.lineBoxes.push({
        left: Math.min(...boxes.map((b) => b.left)),
        right: Math.max(...boxes.map((b) => b.right)),
        top: Math.min(...boxes.map((b) => b.top)),
        bottom: Math.max(...boxes.map((b) => b.bottom)),
      });
      const raws = els.map((el) => Number(el.dataset.rawIdx));
      m.tokRaw.push(raws);
      m.gapRaw.push(
        gapRawForLine(
          raws,
          els.map((el) => (el.dataset.frag ?? 'only') as 'only' | 'first' | 'mid' | 'last')
        )
      );
    }

    // An empty line after a trailing line break has no token boxes: synthesize
    // its band just below the last line so a drop there lands AFTER the break.
    const trail = root.querySelector<HTMLElement>(
      `[data-slot-trail-zone="${CSS.escape(col.zoneKey)}"]`
    );
    if (trail && m.lineBoxes.length > 0) {
      const last = m.lineBoxes[m.lineBoxes.length - 1];
      const h = last.bottom - last.top;
      m.els.push([]);
      m.tokenBoxes.push([]);
      m.lineBoxes.push({
        left: last.left,
        right: last.right,
        top: last.bottom,
        bottom: last.bottom + h,
      });
      m.tokRaw.push([]);
      m.gapRaw.push([Number(trail.dataset.slotTrailRaw) + 1]);
    }

    // An EMPTY column: nothing opens, the surface's own column drop takes it.
    if (m.lineBoxes.length === 0) {
      clearShifts();
      const target: SlotDropTarget = {
        zoneKey: col.zoneKey,
        kind: 'empty',
        rawIdx: 0,
        newLine: false,
      };
      lastTarget.current = target;
      const key = `${col.zoneKey}|empty`;
      if (key !== lastKey.current) {
        lastKey.current = key;
        setUi({ target, caret: null, ring: null, label: `Add to ${col.label}` });
      }
      return target;
    }

    const band = (side: 'above' | 'below'): Box | null => {
      const el = root.querySelector<HTMLElement>(
        `[data-slot-newline="${side}"][data-slot-zone="${CSS.escape(col.zoneKey)}"]`
      );
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { left: r.left, right: r.right, top: r.top, bottom: r.bottom };
    };
    const newLine = { above: band('above'), below: band('below') };
    const mt = (metrics.current ??= readDragMetrics());
    const hyst = mt.hystPx;
    const prev = prevZone.current === col.zoneKey ? prevSlot.current : null;

    // Replace arms only after the pointer has rested on the same token centre.
    let slot = slotForPointer(m.lineBoxes, m.tokenBoxes, p.x, p.y, prev, hyst, {
      replace: false,
      newLine,
    });
    const withReplace = slotForPointer(m.lineBoxes, m.tokenBoxes, p.x, p.y, prev, hyst, {
      replace: true,
      newLine,
    });
    const rk =
      withReplace && withReplace.kind === 'replace'
        ? `${col.zoneKey}|${withReplace.line}|${withReplace.index}`
        : null;
    const now = performance.now();
    const step = stepRest(rest.current, rk, p.x, p.y, now, mt.restMs, mt.hystPx);
    if (rk != null && (rest.current?.key !== rk || rest.current !== step.rest)) {
      // (Re)started: wake once the rest window has passed so the ring can arm.
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        if (pointer.current && !raf.current) raf.current = requestAnimationFrame(evaluate);
      }, mt.restMs + 16);
    }
    rest.current = step.rest;
    if (step.armed && withReplace) slot = withReplace;
    if (!slot) return null;

    const rawIdx = rawIndexForSlot(slot, m.gapRaw, m.tokRaw);
    const target: SlotDropTarget = {
      zoneKey: col.zoneKey,
      kind: slot.kind,
      rawIdx,
      newLine: slot.kind === 'newline-above' || slot.kind === 'newline-below',
    };
    prevSlot.current = slot;
    prevZone.current = col.zoneKey;
    lastTarget.current = target;

    // ── open the gap: neighbours slide apart (relative offset, no reflow) ──
    const session = getZoneDrag();
    const w = session && session.widthPx > 0 ? session.widthPx : mt.gapPx;
    const open = slot.kind === 'slot';
    m.els.forEach((els, li) => {
      els.forEach((el, ti) => {
        let shift = 0;
        if (open && li === slot.line) {
          const after = ti >= slot.index;
          if (col.align === 'left') shift = after ? w : 0;
          else if (col.align === 'right') shift = after ? 0 : -w;
          else shift = after ? w / 2 : -w / 2;
        }
        const want = shift ? `${shift}px` : '';
        if (el.style.left !== want) el.style.left = want;
      });
    });

    const key = `${col.zoneKey}|${slot.kind}|${slot.line}|${slot.index}`;
    if (key !== lastKey.current) {
      lastKey.current = key;
      const toLocal = (b: Box) => ({
        left: (b.left - rr.left) / scale,
        top: (b.top - rr.top) / scale,
        width: (b.right - b.left) / scale,
        height: (b.bottom - b.top) / scale,
      });
      let caret: SlotUi['caret'] = null;
      let ring: SlotUi['ring'] = null;
      let label: string | null = null;
      if (slot.kind === 'slot') {
        const lb = m.lineBoxes[slot.line];
        const g =
          m.tokenBoxes[slot.line].length > 0
            ? gapX(m.tokenBoxes[slot.line], slot.index)
            : col.align === 'right'
              ? lb.right
              : col.align === 'center'
                ? (lb.left + lb.right) / 2
                : lb.left;
        const cx =
          col.align === 'left'
            ? g + (w * scale) / 2
            : col.align === 'right'
              ? g - (w * scale) / 2
              : g;
        caret = {
          left: (cx - rr.left) / scale,
          top: (lb.top - rr.top) / scale,
          height: (lb.bottom - lb.top) / scale,
        };
      } else if (slot.kind === 'replace') {
        ring = toLocal(m.tokenBoxes[slot.line][slot.index]);
        label = 'Replace';
      } else {
        label = 'New line';
      }
      setUi({ target, caret, ring, label });
    }
    return target;
  }, [rootRef, clearShifts]);

  const schedule = React.useCallback(() => {
    if (!raf.current) raf.current = requestAnimationFrame(evaluate);
  }, [evaluate]);

  // Esc / a drop anywhere / the source ending all end the targeting.
  React.useEffect(() => {
    if (!dragging) return;
    const end = () => clear();
    window.addEventListener('dragend', end, true);
    // No window `drop` listener: it would run BEFORE React's onDrop and wipe
    // the shown slot. onDrop clears after it has used it.
    return () => {
      window.removeEventListener('dragend', end, true);
    };
  }, [dragging, clear]);

  React.useEffect(() => clear, [clear]);

  const onDragOver = React.useCallback(
    (e: React.DragEvent) => {
      if (!enabled || !hasPayload(e)) return;
      e.preventDefault();
      pointer.current = { x: e.clientX, y: e.clientY };
      if (!draggingRef.current) {
        draggingRef.current = true;
        setDragging(true);
      }
      schedule();
    },
    [enabled, schedule]
  );

  const onDragLeave = React.useCallback(
    (e: React.DragEvent) => {
      const root = rootRef.current;
      if (root && root.contains(e.relatedTarget as Node | null)) return;
      clear();
    },
    [rootRef, clear]
  );

  /** Returns true when the drop was a slot drop and has been handled. */
  const onDrop = React.useCallback(
    (e: React.DragEvent): boolean => {
      if (!enabled || !hasPayload(e)) return false;
      pointer.current = { x: e.clientX, y: e.clientY };
      cancelAnimationFrame(raf.current);
      raf.current = 0;
      // Release uses the slot the architect SAW (rested Replace, hysteresis
      // held); a fresh evaluation is only the fallback.
      const target = pickReleaseTarget(lastTarget.current, () => evaluate());
      if (!target || target.kind === 'empty') {
        clear();
        endZoneDrag();
        return false;
      }
      e.preventDefault();
      const handled = optsRef.current.onDropAt(target, e);
      // Unhandled drops keep bubbling to the band's own column drop.
      if (handled) e.stopPropagation();
      clear();
      endZoneDrag();
      return handled;
    },
    [enabled, evaluate, clear]
  );

  return { ui, dragging, handlers: { onDragOver, onDragEnter: onDragOver, onDragLeave, onDrop } };
}

/** What a slot drop needs from its surface — the same three model calls on the
 *  sheet and in the panel, so a drop means the same thing in both. */
export interface SlotDropHandlers {
  insertAt: (
    zoneKey: string,
    idx: number,
    value: string,
    srcZone: string | null,
    srcIdx: number | null,
    newLine: boolean
  ) => void;
  replaceAt: (
    zoneKey: string,
    idx: number,
    value: string,
    srcZone: string | null,
    srcIdx: number | null
  ) => void;
  moveTokensAt: (
    zoneKey: string,
    idx: number,
    keys: string[],
    copy: boolean,
    newLine: boolean
  ) => void;
}

/** Read the drop payload and run it at the slot. True when handled. */
export function applySlotDrop(
  target: SlotDropTarget,
  e: React.DragEvent,
  h: SlotDropHandlers
): boolean {
  let d: {
    type?: string;
    value?: string;
    sourceZone?: string | null;
    sourceIndex?: number | null;
    copy?: boolean;
    keys?: string[];
  } | null = null;
  try {
    d = JSON.parse(e.dataTransfer.getData('application/json'));
  } catch {
    return false;
  }
  if (!d) return false;
  const { zoneKey, rawIdx, newLine, kind } = target;
  // A whole multi-selection, or a Ctrl-drag copy, lands AT the slot in key order
  // (Replace is single-token: a group dropped on a token's centre inserts there).
  if (d.type === 'tokenMulti' && d.keys?.length) {
    h.moveTokensAt(zoneKey, rawIdx, d.keys, !!d.copy, newLine);
    return true;
  }
  if (d.type === 'token' && d.copy && d.sourceZone != null && d.sourceIndex != null) {
    h.moveTokensAt(zoneKey, rawIdx, [`token:${d.sourceZone}:${d.sourceIndex}`], true, newLine);
    return true;
  }
  if (d.value == null) return false;
  const srcZone = d.sourceZone ?? null;
  const srcIdx = d.sourceIndex ?? null;
  if (kind === 'replace') {
    h.replaceAt(zoneKey, rawIdx, d.value, srcZone, srcIdx);
    return true;
  }
  // Dropping a token back where it already is changes nothing — no phantom undo step.
  if (
    !newLine &&
    srcZone === zoneKey &&
    srcIdx != null &&
    (srcIdx === rawIdx || srcIdx + 1 === rawIdx)
  ) {
    return true;
  }
  h.insertAt(zoneKey, rawIdx, d.value, srcZone, srcIdx, newLine);
  return true;
}
